import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  useContentToken,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';

/**
 * Village directory for the Wine & Dine page: the shops, tasting rooms,
 * and restaurants that sit in Woodin Creek Village. Content is the live
 * site's tenant list, editable per place.
 */

type FilterId = 'all' | 'shopping' | 'wine' | 'dine';

const place1: SlotGroupDefinition = {
  label: "Dashing Nails",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/DashingNails.jpg",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Dashing Nails logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "DASHING NAILS",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "shopping",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17255 135th Ave NE Bldg 2A Suite 1050, Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/Cj71qtaxV5EfDqzS8",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 949-5082",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://dashingnailsalon.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Experience the ultimate in beauty and relaxation at Dashing Nails & Spa, where convenience meets luxury. Discover top-tier nail care and waxing services, using mostly natural and organic products for your well-being. Trust our certified, skilled professionals who are passionate about fulfilling all your beauty needs. Join us for a rejuvenating experience and let us lavish you with pampering.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place2: SlotGroupDefinition = {
  label: "Fresh Cuts",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/fresh+cuts-8f6c5e73.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Fresh Cuts logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "FRESH CUTS",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "shopping",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17255 135th Ave NE Bldg 2A Suite 1045, Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/BGfvspqwVvnAnN8U6",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425)-892-2062",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://freshcutsalon.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Fresh Cuts is a diverse family friendly establishment with a vast amount of knowledge and experience. We strive to help you feel and look your best and offer a large range of services from a simple buzz cut to a complete change in style and color. Visit our website to see our complete list of services. Walk-ins are always welcome. We hope to see you soon! Social <a href=\"https://www.instagram.com/freshcuts_barber_hairsalon/\" target=\"_blank\" rel=\"noopener noreferrer\">@Freshcuts</a></p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place3: SlotGroupDefinition = {
  label: "Alexandria Nicole Cellars",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/anc.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Alexandria Nicole Cellars logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "ALEXANDRIA NICOLE CELLARS",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17140 135th Ave NE Bldg 2D Suite 1010 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/fTH4EtUEAViYi4Vk8",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 487-9463",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.alexandrianicolecellars.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Located on 327-acres overlooking the majestic Columbia River in the Horse Heaven Hills Appellation, our estate winery is designed to produce small lots of hand-crafted wines. We believe that quality starts in the vineyard and continues into the cellar. Enjoy tasting our elegantly approachable wines at our newest location Woodin Creek Tasting Room. We offer elevated customer wine experiences complete with outdoor seating and small bites for those perfect Woodinville summer nights. Stop by for a perfect pairing wine experience or just a bottle to share with great company! Wine Club Members will have exclusive access to Hidden Door – our Wine Club Members only room. Come for the wines, stay for the people!</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place4: SlotGroupDefinition = {
  label: "Baer Winery",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/4.webp",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Baer Winery logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "BAER WINERY",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13590 NE Village Square Drive Bldg 2E Suite1025 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/yYQ2nnsFQD16VqiV8",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 483-7060",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.baerwinery.com",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Lance Baer founded Baer Winery in 2000 with a single passion — to bring something unique to Woodinville’s growing wine community.</p><p>Baer Winery continues as a family-owned winery dedicated to producing handcrafted Bordeaux-style red blends, and red and white single varietal wines that showcase the exceptional fruit from Stillwater Creek Vineyard, our select Columbia Valley partner and one of Washington’s newest AVAs – The Royal Slope. We are honored that our wines, and winemaker Erica Orr, continue to receive national acclaim, including appearances in Wine Spectator’s Top 100 wines.</p><p>We look forward to welcoming you to our Woodin Creek Village tasting room, with tasting flights and glass pours of your favorite Baer wines, as well as special club member events.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place5: SlotGroupDefinition = {
  label: "Bayernmoor Cellars",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/Bayernmoor_Cellars--283-29.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Bayernmoor Cellars logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "BAYERNMOOR CELLARS",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17140 135th Ave NE Suite 1040 Woodinville, WA 90872, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/L6iPfxZ9TZPYxVoT6",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 216-3300",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.bayernmoor.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Wednesday-Friday 1pm-7pm</p><p>Saturday 12pm-7pm</p><p>Sunday 12pm-6pm</p><p>Bayernmoor Cellars is the 2024 Washington Winery of the Year (Great Northwest Wine) and the 2023 Winery of the Year (Seattle/WA State Wine Awards)! We are a family-owned winery with an estate Pinot Noir vineyard just outside of Stanwood, WA, focused on producing world class wines using sustainable farming methods right here in the Pacific Northwest. Every Bayernmoor vintage is a celebration of tradition with a bit of modern zest, blending art, science and innovation to create award winning wines. Immerse yourself in an atmosphere of elevated fun and warmth at our Tasting Room for an unforgettable experience! Whether indulging in wine flights curated with exquisite seasonal bites, or savoring a glass of your favorite varietal, every moment is crafted for pleasure. Book your experience now and mark your calendar for a day you won’t forget!</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place6: SlotGroupDefinition = {
  label: "Brian Carter Cellars",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/briancarter.jpg",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Brian Carter Cellars logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "BRIAN CARTER CELLARS",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17140 135th Ave NE Bldg 2D Suite 1030 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/pQ9RaLGXCKmsV6qk6",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 806-9463",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.briancartercellars.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Open any bottle of Brian Carter Cellars wine and you’ll discover both an inspiration and a story. Our handcrafted wines reflect classic elegance and the centuries-old tradition found in true European-style blends. Balanced and well-rounded, each wine speaks anew to the art of blending and showcases Brian’s personal passion for making exemplary European-style blends from Washington State’s unique terroir.</p><p>Just as an artist ponders each color possibility, winemaker Brian Carter deliberates long and hard over his broad palette of grape varietals, blending around the edges to create an inspired new world representation of each European-style he makes. Brian didn’t set out to be a blender. His art grew out of science and agriculture, and in fact, one wasn’t possible without the other. Since coming to Washington State in 1980, Brian has been at the forefront of the Washington wine industry. By encouraging growers to plant different varietals, he influenced both the quantity and quality of Washington State varietals, and tremendous improvements in the industry. In turn he was able to develop the concept for Brian Carter Cellars and his passion for blending.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place7: SlotGroupDefinition = {
  label: "Rooted Souls Wine",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/13.webp",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Rooted Souls Wine logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "ROOTED SOULS WINE",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13590 NE Village Square Drive Bldg 2E Suite 1020 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/uUi9YSSF2c6XQoCH9",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 765-9222",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.rootedsoulswine.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Where modern sophistication meets delightful fun – our boutique winery founded in 2018 is known for an exceptional selection of rosé wines, ensuring a vibrant and unforgettable experience for wine enthusiasts and novices alike. From crisp and refreshing to luscious and fruit-forward, our selection boasts a palette of flavors that will delight and surprise your tastebuds.</p><p>Much of our fruit is sourced from Red Mountain and Yakima Valley AVAs, working with Washington State Certified Sustainable vineyards. Rooted Souls is a labor of love by Owners/Winemaker, Steve & Erica Carmody, along with their incredible team.</p><p>Come and experience the joy of rosé, along with an excellent selection of whites and reds, where modern elegance, playful charm, and exquisite wines converge for an awesome afternoon with the Rooted Souls family.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place8: SlotGroupDefinition = {
  label: "Truthteller Winery",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/4-1.jpg",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Truthteller Winery logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "TRUTHTELLER WINERY",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13590 NE Village Square Drive Bldg 2E Suite 1045 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/HWQZUXnezVUpbnRE6",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(253) 948-7046",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.truthtellerwinery.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Folks often ask us about our name and our frolicking Jester logo. Friends who knew of the Swiss origin of our name once told us it translates into “village idiot”. We continued to investigate (wouldn’t you?) and discovered the translation is actually closer to “jester” – an entertainer who tells the truth through comedy. We think the Jester and wine share the trait of balancing truth and pleasure.</p><p>Come Seek the Truth!</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place9: SlotGroupDefinition = {
  label: "Tsillan Cellars",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/big-Tsillan-Logo-Gold---Copy-d5a07e6e.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Tsillan Cellars logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "TSILLAN CELLARS",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13550 NE Village Square Drive, Unit 1070, Woodinville, WA 98072",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/QydkXmHz6o7PjyobA",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 482-5976",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://tsillancellars.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Tsillan Cellars, an Italian country retreat recreated on the gently sloping shores of deep water Lake Chelan, offers a destination experience where you will be transported to the beauty and serenity of Italy’s Lake Como. Our winery is proud to produce estate wines from the Chelan AVA along with non-estate wines from some of Washington’s best growing regions. We welcome you to join us in Woodinville at Wine Alley in our beautiful tasting room designed with the elegance, richness, and comfort of the Italian lifestyle-- or  &quot;Dolce Vita&quot;. Try our dynamic, food-friendly wines along with small bites from our kitchen. Our staff would love to host you for a tasting or ask us about hosting your own private event or party!</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place10: SlotGroupDefinition = {
  label: "Woodinville Wine Country",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/thumb-WWC-Logo-Stacked-Red-16ad9b29.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Woodinville Wine Country logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "WOODINVILLE WINE COUNTRY",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "wine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13590 NE Village Square Drive Suite 1030 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/8VcM1s6GJRy3y6Mk9",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 287-3298",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "http://www.woodinvillewinecountry.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Visitor Center hours: Monday-Friday: 11am to 3pm, </p><p>Sat-Sun: Coming Soon</p><p>At Woodinville Wine Country Visitor Center, we're your gateway to exploring Washington's premier wine region. Offering concierge-level service, we help plan your perfect itinerary to taste legendary wines, discover craft breweries, and explore artisan distilleries. Stay updated with our event calendar for local happenings and shop our curated selection of unique keepsakes and wine accessories. Your one-stop-shop for an unforgettable Woodinville experience!</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place11: SlotGroupDefinition = {
  label: "Brix Wine Café",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/Brix-Logo-page-001.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Brix Wine Café logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "BRIX WINE CAFÉ",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "dine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13550 NE Village Square Drive Bldg 2C # 1050, Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/DnX8KSpzKhGh5t6w6",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 242-0280",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.brixwinecafe.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Locally owned and located in the heart of Juanita for thirteen years, Brix Wine Café has established itself as a welcoming place for friends to gather, celebrate and relax. We are thrilled to be moving to Woodin Creek Village and expand our menu created by Executive Chef, Aaron Lee, while offering the same genuine hospitality under the direction of General Manager, Daniel Baldwin in a gorgeous new space including a year-round covered patio. We hope Brix will become your favorite spot to enjoy yourself in a stunning atmosphere that’s always comfortable, never pretentious. Cheers!</p><p>For updates on our progress, please follow us on Instagram <a href=\"https://www.instagram.com/brixwoodincreek/\" target=\"_blank\" rel=\"noopener noreferrer\">@brixwoodincreek</a></p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place12: SlotGroupDefinition = {
  label: "Sidekick Coffee",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/15.webp",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Sidekick Coffee logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "SIDEKICK COFFEE",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "dine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17255 135th Ave NE Bldg 2A Suite 1020, Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/5WnWSUN3koNBBbZeA",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425)-949-7422",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.sidekick.coffee/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Monday through Friday 6am-5pm</p><p>Saturday and Sunday 7am-5pm</p><p>Sidekick Coffee is Woodinville’s long-awaited independent coffee shop. Locally owned and community minded, you can rely on us for warmth, hospitality, and great coffee. We proudly serve Herkimer roasted coffee, pastries baked in-house, and locally sourced tasty eats. Take a coffee and treat with you on your adventure or sit down and stay awhile in our bright and refreshing space. Coffee makes a great sidekick. #coffeeisyourfriend.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place13: SlotGroupDefinition = {
  label: "Woodin Creek Kitchen and Tap",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/18.webp",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Woodin Creek Kitchen and Tap logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "WOODIN CREEK KITCHEN AND TAP",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "dine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13550 NE Village Square Drive Bldg 2C # 1010, Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/8YogvdwDvFemvGzaA",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 215-1999",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.woodincreekkitchen.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Woodin Creek Kitchen & Tap is the second restaurant venture on the eastside for Tim Short and Chef Chris Nelson and offers a spacious dining experience for the whole family. Our menu has something for everyone. In addition, we have an extensive bar with 30 beers on tap and multiple tv screens to catch all the action of your favorite teams. Looking to have a private party? We’ve got that covered too! We can’t wait to see you.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place14: SlotGroupDefinition = {
  label: "Rusty Pelican Café",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/14.webp",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Rusty Pelican Café logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "RUSTY PELICAN CAFÉ",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "dine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13590 NE Village Square Drive Bldg 2E Suite1060 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/SR67kK93HnjpYiJP9",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425)-286-6660",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "http://rustypelicancafe.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Rusty Pelican Cafés are family-owned. Originally from Chicago, they had a restaurant for 25 years and moved to the Seattle area in 2000, where they opened the first Rusty Pelican Café in 2002. Rusty Pelican Café then moved to the beautiful town of Edmonds in 2012, and about a year later opened a second location in Mill Creek. Now the Rusty Pelican Café family has opened their third restaurant here at Woodin Creek Village.</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place15: SlotGroupDefinition = {
  label: "Swanky Scoop",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://irp.cdn-website.com/f79dba72/dms3rep/multi/SwankyScoop_Logo.png",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Swanky Scoop logo",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "SWANKY SCOOP",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "dine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "13550 NE Village Square Drive Bldg 2C Suite 1030 Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://goo.gl/maps/DnX8KSpzKhGh5t6w6",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "(425) 659-3534",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://swankyscoop.com/",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Any day is a great day for ice cream! Swanky Scoop is a locally owned small batch ice cream shop. We churn all our flavors on-site and offer a rotating selection of 12 dairy and 8 non-dairy flavors including favorites like Cookie Monster, Death by Chocolate, and vegan Mint Matcha Chip. Check our website for today's options! Freshly rolled waffle cones, milkshakes, custom ice cream cakes, pints to go, sundaes, and root beer floats...our award-winning ice cream turns any evening into an event. Our non-dairy flavors are vegan, nut free, and added gluten free, and we have vegan and gluten free waffle cones, cookies, cakes, and milkshake options. Open Tuesday - Sunday in the winter, and every day Memorial Day through Labor Day. </p><p><strong>Socials</strong></p><p><a href=\"http://www.instagram.com/swankyscoop\" target=\"_blank\" rel=\"noopener noreferrer\">www.instagram.com/swankyscoop</a></p><p><a href=\"http://www.facebook.com/SwankyScoop\" target=\"_blank\" rel=\"noopener noreferrer\">www.facebook.com/SwankyScoop</a></p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const place16: SlotGroupDefinition = {
  label: "Ma‘ono Fried Chicken",
  fields: {
    image: {
      label: 'Logo',
      type: 'image',
      defaultValue: "https://images.squarespace-cdn.com/content/v1/678dcb3f2830c94f09790da2/131cab99-d9b9-43b2-9da0-0481359c0302/Location-Woodinville.jpg?format=750w",
    },
    alt: {
      label: 'Logo Alt Text',
      type: 'text',
      defaultValue: "Ma‘ono Fried Chicken storefront in Woodin Creek Village",
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: "MA‘ONO FRIED CHICKEN",
    },
    category: {
      label: 'Category',
      type: 'text',
      defaultValue: "dine",
      description: 'Filter group: shopping, wine, or dine.',
    },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: "17255 135th Ave NE, Woodinville, WA 98072, United States",
    },
    map_url: {
      label: 'Map URL',
      type: 'url',
      defaultValue: "https://maps.app.goo.gl/kXRdU5UiAfg8nV3F7",
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultValue: "",
    },
    website: {
      label: 'Website',
      type: 'url',
      defaultValue: "https://www.maonoseattle.com/woodinville",
    },
    description: {
      label: 'Description',
      type: 'richtext',
      defaultValue: "<p>Hawaiian-style picnic chicken from Ma‘ono, now open for dine in and takeout in Woodin Creek Village.</p><p><strong>Hours</strong></p><p>Mon - Tues: Closed</p><p>Wed - Fri: 3PM to 9PM</p><p>Sat &amp; Sun: 11AM to 9PM</p>",
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const places: { path: string; definition: SlotGroupDefinition }[] = [
    { path: 'winedinedirectory.place1', definition: place1 },
    { path: 'winedinedirectory.place2', definition: place2 },
    { path: 'winedinedirectory.place3', definition: place3 },
    { path: 'winedinedirectory.place4', definition: place4 },
    { path: 'winedinedirectory.place5', definition: place5 },
    { path: 'winedinedirectory.place6', definition: place6 },
    { path: 'winedinedirectory.place7', definition: place7 },
    { path: 'winedinedirectory.place8', definition: place8 },
    { path: 'winedinedirectory.place9', definition: place9 },
    { path: 'winedinedirectory.place10', definition: place10 },
    { path: 'winedinedirectory.place11', definition: place11 },
    { path: 'winedinedirectory.place12', definition: place12 },
    { path: 'winedinedirectory.place13', definition: place13 },
    { path: 'winedinedirectory.place14', definition: place14 },
    { path: 'winedinedirectory.place15', definition: place15 },
    { path: 'winedinedirectory.place16', definition: place16 },
];

function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
}

function MapPinIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="wine-dine-directory__icon" aria-hidden="true">
      <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="wine-dine-directory__icon" aria-hidden="true">
      <path fill="currentColor" d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="wine-dine-directory__icon" aria-hidden="true">
      <path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.93 9h-3.07a15.7 15.7 0 0 0-1.2-5.24A8.03 8.03 0 0 1 18.93 11zM12 4.07c.7 1 1.95 3.05 2.43 5.93H9.57C10.05 7.12 11.3 5.07 12 4.07zM4.26 13a8.03 8.03 0 0 1 0-2h3.07c-.12.66-.2 1.33-.2 2s.08 1.34.2 2H4.26zm.81 2h3.07c.3 1.97.9 3.78 1.2 5.24A8.03 8.03 0 0 1 5.07 15zM9.57 14h4.86c-.48 2.88-1.73 4.93-2.43 5.93-.7-1-1.95-3.05-2.43-5.93zm5.23 5.24c.3-1.46.9-3.27 1.2-5.24h3.07a8.03 8.03 0 0 1-4.27 5.24zM16.67 13c.12-.66.2-1.33.2-2s-.08-1.34-.2-2h3.07a8.03 8.03 0 0 1 0 4h-3.07zM8.27 4.76A15.7 15.7 0 0 0 7.07 11H4.07a8.03 8.03 0 0 1 4.2-6.24z" />
    </svg>
  );
}

function PlaceCard({
  path,
  definition,
  filter,
  stripe,
}: {
  path: string;
  definition: SlotGroupDefinition;
  filter: FilterId;
  stripe: number;
}) {
  const place = useSlotGroup(path, definition);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const category = (place.category || '').toLowerCase();
  const matches = filter === 'all' || category === filter;
  if (!place.name && !isEditing) return null;
  if (!matches && !isEditing) return null;

  const target = place._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <EditableSlotGroup
      groupPath={path}
      definition={definition}
      className={cn(
        'wine-dine-directory__row px-md py-2xl md:px-xl',
        stripe % 2 === 1 ? 'bg-muted' : 'bg-background',
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-xl md:flex-row md:items-start md:gap-2xl">
        <div className="flex w-full shrink-0 justify-center md:w-[34%]">
          {place.image ? (
            <VibesitesImage
              url={place.image}
              alt={place.alt || place.name || ''}
              width={300}
              height={300}
              sizes="300px"
              objectFit="cover"
              className="wine-dine-directory__logo h-[200px] w-[200px] rounded-full border border-border bg-card object-cover lg:h-[300px] lg:w-[300px]"
            />
          ) : (
            <div className="flex h-[200px] w-[200px] items-center justify-center rounded-full border border-border bg-card lg:h-[300px] lg:w-[300px]">
              <span className="px-md text-center font-heading text-sm uppercase tracking-wide text-muted-foreground">
                {place.name}
              </span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 text-center md:text-left">
          <h2 className="font-heading text-3xl font-bold uppercase leading-tight text-foreground lg:text-4xl">
            {place.name}
          </h2>
          <ul className="mt-md space-y-xs font-body text-sm font-semibold text-secondary">
            {place.address ? (
              <li className="flex items-start justify-center gap-sm md:justify-start">
                <MapPinIcon />
                {place.map_url ? (
                  <a href={place.map_url} target={target} rel={rel} className="text-secondary hover:underline">
                    {place.address}
                  </a>
                ) : (
                  <span>{place.address}</span>
                )}
              </li>
            ) : null}
            {place.phone ? (
              <li className="flex items-start justify-center gap-sm md:justify-start">
                <PhoneIcon />
                <a href={telHref(place.phone)} className="text-secondary hover:underline">
                  {place.phone}
                </a>
              </li>
            ) : null}
            {place.website ? (
              <li className="flex items-start justify-center gap-sm md:justify-start">
                <GlobeIcon />
                <a href={place.website} target={target} rel={rel} className="break-all text-secondary hover:underline">
                  {place.website}
                </a>
              </li>
            ) : null}
          </ul>
          {place.description ? (
            <div
              className="wine-dine-directory__body mt-md font-body text-base leading-relaxed text-foreground"
              dangerouslySetInnerHTML={{ __html: place.description }}
            />
          ) : null}
        </div>
      </div>
    </EditableSlotGroup>
  );
}

export function WineDineDirectory() {
  const [filter, setFilter] = useState<FilterId>('all');
  const filterAll = useContentToken('winedinedirectory.filter_all');
  const filterShopping = useContentToken('winedinedirectory.filter_shopping');
  const filterWine = useContentToken('winedinedirectory.filter_wine');
  const filterDine = useContentToken('winedinedirectory.filter_dine');
  const filters: { id: FilterId; path: string; label: string }[] = [
    { id: 'all', path: 'winedinedirectory.filter_all', label: filterAll || 'All' },
    { id: 'shopping', path: 'winedinedirectory.filter_shopping', label: filterShopping || 'Shopping & Services' },
    { id: 'wine', path: 'winedinedirectory.filter_wine', label: filterWine || 'Wine' },
    { id: 'dine', path: 'winedinedirectory.filter_dine', label: filterDine || 'Dine' },
  ];

  return (
    <section className="bg-background">
      <div className="px-md py-xl md:px-xl">
        <div className="flex flex-col items-stretch justify-center gap-xs sm:flex-row sm:flex-wrap sm:items-center" role="tablist" aria-label="Directory categories">
          {filters.map((item) => {
            const selected = filter === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(item.id)}
                className={cn(
                  'bg-secondary px-xl py-md font-body text-sm font-semibold uppercase tracking-wide text-secondary-foreground transition-colors',
                  selected && 'wine-dine-directory__tab--active',
                )}
              >
                <EditableToken path={item.path} as="span">
                  {item.label}
                </EditableToken>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {places.map((place, index) => (
          <PlaceCard
            key={place.path}
            path={place.path}
            definition={place.definition}
            filter={filter}
            stripe={index}
          />
        ))}
      </div>

      <EditableToken path="winedinedirectory.filter_all" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="winedinedirectory.filter_shopping" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="winedinedirectory.filter_wine" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="winedinedirectory.filter_dine" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <EditableSlotGroup
        groupPath="winedinedirectory.place1"
        definition={place1}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place2"
        definition={place2}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place3"
        definition={place3}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place4"
        definition={place4}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place5"
        definition={place5}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place6"
        definition={place6}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place7"
        definition={place7}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place8"
        definition={place8}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place9"
        definition={place9}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place10"
        definition={place10}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place11"
        definition={place11}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place12"
        definition={place12}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place13"
        definition={place13}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place14"
        definition={place14}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place15"
        definition={place15}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="winedinedirectory.place16"
        definition={place16}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .wine-dine-directory__tab--active {
          background: color-mix(in srgb, var(--vibe-color-secondary) 62%, #000);
        }
        .wine-dine-directory__logo {
          display: block;
          object-fit: cover;
          object-position: center;
        }
        .wine-dine-directory__icon {
          width: 1.15rem;
          height: 1.15rem;
          flex-shrink: 0;
          margin-top: 0.15rem;
          color: var(--vibe-color-secondary);
        }
        .wine-dine-directory__body p {
          margin: 0 0 0.75rem;
        }
        .wine-dine-directory__body p:last-child {
          margin-bottom: 0;
        }
        .wine-dine-directory__body a {
          color: var(--vibe-color-secondary);
          text-decoration: underline;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'WineDineDirectory',
  displayName: 'Wine & Dine Directory',
  tokens: {
    'winedinedirectory.filter_all': {
      label: 'All Filter Label',
      type: 'text',
      defaultValue: 'All',
    },
    'winedinedirectory.filter_shopping': {
      label: 'Shopping Filter Label',
      type: 'text',
      defaultValue: 'Shopping & Services',
    },
    'winedinedirectory.filter_wine': {
      label: 'Wine Filter Label',
      type: 'text',
      defaultValue: 'Wine',
    },
    'winedinedirectory.filter_dine': {
      label: 'Dine Filter Label',
      type: 'text',
      defaultValue: 'Dine',
    },
  },
  slotGroups: {
    'winedinedirectory.place1': place1,
    'winedinedirectory.place2': place2,
    'winedinedirectory.place3': place3,
    'winedinedirectory.place4': place4,
    'winedinedirectory.place5': place5,
    'winedinedirectory.place6': place6,
    'winedinedirectory.place7': place7,
    'winedinedirectory.place8': place8,
    'winedinedirectory.place9': place9,
    'winedinedirectory.place10': place10,
    'winedinedirectory.place11': place11,
    'winedinedirectory.place12': place12,
    'winedinedirectory.place13': place13,
    'winedinedirectory.place14': place14,
    'winedinedirectory.place15': place15,
    'winedinedirectory.place16': place16,
  },
};

(WineDineDirectory as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default WineDineDirectory;
